import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLang } from "@/context/LanguageContext";
import { Plus, Trash2 } from "lucide-react";

const newRow = () => ({
  id:
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  name_en: "",
  name_ar: "",
  price: "",
  calories: "",
  protein_g: "",
  carbs_g: "",
  fat_g: "",
});

/**
 * Editable table of variants (sizes or milk options). Each row has its own
 * name (EN/AR) + price + calories + macros. Column headers label every field.
 */
export default function VariantsEditor({ title, rows = [], onChange, testid }) {
  const { t, isRtl } = useLang();

  const update = (idx, key, value) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, [key]: value } : r));
    onChange(next);
  };
  const addRow = () => onChange([...rows, newRow()]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));

  const cols = [
    { key: "name_en", label: t.nameEnShort, w: "min-w-[120px]", type: "text" },
    { key: "name_ar", label: t.nameArShort, w: "min-w-[120px]", type: "text" },
    { key: "price", label: t.price, w: "w-20", type: "number" },
    { key: "calories", label: t.calShort, w: "w-20", type: "number" },
    { key: "protein_g", label: t.protShort, w: "w-20", type: "number" },
    { key: "carbs_g", label: t.carbShort, w: "w-20", type: "number" },
    { key: "fat_g", label: t.fatShort, w: "w-20", type: "number" },
  ];

  return (
    <div className="rounded-2xl border border-black/10 p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-[0.2em] text-[#0F5F63] font-semibold">{title}</p>
        <Button type="button" variant="outline" size="sm" onClick={addRow}
          data-testid={`${testid}-add`}
          className="rounded-full border-[#0F5F63]/30 text-[#0F5F63] hover:bg-[#F4F2EB] gap-1 h-8">
          <Plus className="w-3.5 h-3.5" /> {t.addRow}
        </Button>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-[#5C6B6D] py-2">{t.noVariants}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-separate border-spacing-y-1" dir={isRtl ? "rtl" : "ltr"}>
            <thead>
              <tr>
                {cols.map((c) => (
                  <th key={c.key} className={`${c.w} text-[10px] uppercase tracking-wider text-[#5C6B6D] font-medium px-1 text-start`}>
                    {c.label}
                  </th>
                ))}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={row.id || idx}>
                  {cols.map((c) => (
                    <td key={c.key} className="px-1 align-top">
                      <Input
                        type={c.type}
                        step={c.type === "number" ? "any" : undefined}
                        min={c.type === "number" ? "0" : undefined}
                        value={row[c.key] ?? ""}
                        onChange={(e) => update(idx, c.key, e.target.value)}
                        data-testid={`${testid}-${idx}-${c.key}`}
                        className="h-9 text-sm"
                      />
                    </td>
                  ))}
                  <td className="px-1 align-top">
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(idx)}
                      data-testid={`${testid}-${idx}-remove`}
                      className="h-9 w-9 rounded-full text-[#E27B58] hover:bg-[#E27B58]/10">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
